import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import makeStyles from '@mui/styles/makeStyles';
import cx from 'classnames';
import { memo } from 'react';

import { darkGray, lightTan, primary, red500, teal900, white } from '@/theme/colors';

import CircularProgress from '../CircularProgress/CircularProgress';
import FAIcon from '../Icon/FAIcon';

const useStyles = makeStyles({
  fontBold: {
    fontWeight: 'bold',
  },
  fontSmoothing: {
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  circularProgress: {
    color: 'inherit',
    margin: 'auto',
  },
});

const useButtonColors = makeStyles({
  // @ts-expect-error TS(2339): Property 'inverted' does not exist on type '{}'.
  primary: ({ inverted }) => ({
    backgroundColor: inverted ? white : primary,
    color: inverted ? primary : white,
    '&:hover': {
      filter: 'brightness(0.9)',
    },
    // MUI's contained `.Mui-disabled` swaps the fill for action.disabledBackground, a 12% tint
    // that is near-white in light mode, so the white label vanished. Keep the variant's own fill.
    '&:disabled': {
      backgroundColor: inverted ? white : primary,
      color: inverted ? primary : white,
      opacity: 0.6,
    },
  }),
  // @ts-expect-error TS(2339): Property 'inverted' does not exist on type '{}'.
  teal: ({ inverted, fontColor }) => ({
    backgroundColor: inverted ? white : teal900,
    // @ts-expect-error TS(2339): Property 'fontColor' does not exist on type '{}'.
    color: ({ fontColor }) => (inverted ? fontColor || teal900 : white),
    '&:hover': {
      filter: 'brightness(0.9)',
    },
    '&:disabled': {
      backgroundColor: inverted ? white : teal900,
      color: inverted ? fontColor || teal900 : white,
      opacity: 0.6,
    },
  }),
  // @ts-expect-error TS(2339): Property 'inverted' does not exist on type '{}'.
  red: ({ inverted }) => ({
    backgroundColor: inverted ? white : red500,
    color: inverted ? red500 : white,
    '&:hover': {
      filter: 'brightness(0.9)',
    },
    '&:disabled': {
      backgroundColor: inverted ? white : red500,
      color: inverted ? red500 : white,
      opacity: 0.6,
    },
  }),
  // @ts-expect-error TS(2339): Property 'inverted' does not exist on type '{}'.
  lightTan: ({ inverted }) => ({
    backgroundColor: inverted ? darkGray : lightTan,
    color: inverted ? lightTan : darkGray,
    '&:hover': {
      filter: 'brightness(0.95)',
    },
    '&:disabled': {
      backgroundColor: inverted ? darkGray : lightTan,
      color: inverted ? lightTan : darkGray,
      opacity: 0.6,
    },
  }),
  // @ts-expect-error TS(2339): Property 'inverted' does not exist on type '{}'.
  darkGray: ({ inverted }) => ({
    color: inverted ? white : darkGray,
    '&:hover': {
      filter: 'brightness(0.95)',
    },
    '&:disabled': {
      color: darkGray,
      opacity: 0.6,
    },
  }),
  // @ts-expect-error TS(2339): Property 'fontColor' does not exist on type '{}'.
  white: ({ fontColor, inverted }) => ({
    // Semantic intent: a card-surface outlined button. Visible on any surface in either theme.
    color: inverted ? 'var(--surfaceCard)' : (fontColor ?? 'var(--onSurface)'),
    backgroundColor: inverted ? (fontColor ?? 'var(--onSurface)') : 'var(--surfaceCard)',
    border: '1px solid var(--surfaceBorder)',
    '&:hover': {
      filter: 'brightness(0.95)',
    },
    '&:disabled': {
      opacity: 0.6,
    },
  }),
});

const useTextColors = makeStyles({
  // @ts-expect-error TS(2339): Property 'fontColor' does not exist on type '{}'.
  root: ({ fontColor = darkGray }) => ({
    color: fontColor,
    '&:hover': {
      filter: 'brightness(0.85)',
    },
    '&:disabled': {
      opacity: 0.6,
    },
  }),
});

const useSizes = makeStyles({
  tiny: {
    padding: 0,
    fontSize: 12,
  },
  huge: {
    minHeight: 56,
    padding: '14px 24px',
    fontSize: 16,
  },
  normal: {
    minHeight: 36,
    padding: '8px 16px',
    fontSize: 14,
  },
  large: {
    minHeight: 44,
    padding: '10px 20px',
    fontSize: 14,
  },
});

const useSquared = makeStyles({
  squared: {
    // @ts-expect-error TS(2339): Property 'squareSize' does not exist on type '{}'.
    minWidth: (props) => props.squareSize,
    // @ts-expect-error TS(2339): Property 'squareSize' does not exist on type '{}'.
    width: (props) => props.squareSize,
    // @ts-expect-error TS(2339): Property 'squareSize' does not exist on type '{}'.
    height: (props) => props.squareSize,
    verticalAlign: 'top',
    marginRight: 20,
    padding: 0,
  },
});

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color' | 'size'> {
  variant?: 'inverted' | 'text' | 'contained';
  color?:
    | 'white'
    | 'red'
    | 'lightTan'
    | 'teal'
    | 'darkGray'
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning';
  size?: 'tiny' | 'normal' | 'large' | 'huge' | 'small' | 'medium' | 'large';
  fontColor?: string;
  fontSmoothing?: boolean;
  fontBold?: boolean;
  label?: string;
  backgroundColor?: string;
  square?: boolean;
  squareSize?: number;
  submitting?: boolean;
  primary?: boolean; // Legacy prop - extracted to prevent spreading to DOM
}

const Button = memo(
  ({
    variant = 'contained',
    color = 'primary',
    classes = {},
    size = 'large',
    startIcon,
    fontColor,
    fontSmoothing,
    fontBold,
    label,
    backgroundColor,
    style,
    square,
    squareSize = 140,
    submitting,
    primary: _primary, // Extract to prevent spreading to DOM
    ...other
  }: ButtonProps) => {
    const text = useTextColors({ fontColor });
    const colors = useButtonColors({
      fontColor,
      inverted: variant === 'inverted',
    });
    const sizes = useSizes();
    const s = useStyles();
    const squared = useSquared({ squareSize });
    const styles = {
      ...(style || {}),
      ...(backgroundColor ? { backgroundColor } : {}),
    };
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const colorClassName = variant === 'text' ? text.root : colors[color];

    const rootClassname = classes && classes.root;
    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    const sizeClassName = sizes[size];

    const startIconElement =
      typeof startIcon === 'string' ? <FAIcon size={14} icon={startIcon} /> : startIcon;

    const actualFontSmoothing =
      typeof fontSmoothing === 'boolean' ? fontSmoothing : variant !== 'text';

    const actualFontBold = typeof fontBold === 'boolean' ? fontBold : variant !== 'text';

    return (
      <MuiButton
        {...other}
        variant={variant === 'text' ? 'text' : 'contained'}
        color={
          colorClassName
            ? undefined
            : (color as 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'warning')
        }
        size={sizeClassName ? undefined : (size as 'small' | 'medium' | 'large')}
        startIcon={startIconElement}
        style={styles}
        classes={{
          ...classes,
          root: cx({
            [s.fontBold]: actualFontBold,
            [s.fontSmoothing]: actualFontSmoothing,
            [colorClassName]: !!colorClassName,
            [sizeClassName]: !!sizeClassName,
            [squared.squared]: !!square,
            ...(rootClassname && { [rootClassname]: !!rootClassname }),
          }),
        }}
        data-sentry-unmask
      >
        {submitting ? (
          <CircularProgress
            color="inherit"
            size={14}
            thickness={5}
            classes={{
              root: s.circularProgress,
            }}
          />
        ) : (
          label || other.children
        )}
      </MuiButton>
    );
  }
);
Button.displayName = 'Button';

export default Button;
